import React, { useState, useEffect } from 'react';
import * as A from './Slide.styles';
import { useNavigate } from 'react-router-dom';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { reviewService } from 'services/reviewService';
import { memberService } from 'services/memberService';

interface Facility {
  id: number;
  name: string;
  imageURL: string;
}

interface Tag {
  tag: string;
  facilities: Facility[];
}

const Slide: React.FC = () => {
  const [tagFacilities, setTagFacilities] = useState<Tag[]>([]);
  const [selectedTagFacilities, setSelectedTagFacilities] = useState<Facility[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [, setIsLoading] = useState<boolean>(true);
  const isGuest = localStorage.getItem("guestMode") === "true";
  const [userName, setUserName] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const userInfo = await memberService.getMe();
        setUserName(userInfo.name);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };

    fetchUserName();
  }, []);

  useEffect(() => {
    const fetchPopularHashtags = async () => {
      try {
        const data = await reviewService.getPopularHashtags(5);

        // API 응답을 Tag[] 형식으로 변환
        const convertedTags: Tag[] = data.map(hashtag => ({
          tag: hashtag.name,
          facilities: hashtag.facilities.map(facility => ({
            id: facility.id,
            name: facility.name,
            imageURL: facility.thumbnailImage,
          })),
        }));

        setTagFacilities(convertedTags);

        // 첫 번째 태그를 기본 선택
        if (convertedTags.length > 0) {
          setSelectedCategory(convertedTags[0].tag);
        }
      } catch (error) {
        console.error('Failed to fetch popular hashtags:', error);
        setTagFacilities([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPopularHashtags();
  }, []);

  useEffect(() => {
    // tagFacilities가 변경되고 selectedCategory가 비어있으면 첫 번째 태그 선택
    if (tagFacilities.length > 0 && !selectedCategory) {
      setSelectedCategory(tagFacilities[0].tag);
    }
  }, [tagFacilities, selectedCategory]);

  useEffect(() => {
    const selectedTag = tagFacilities.find((tag) => tag.tag === selectedCategory);
    setSelectedTagFacilities(selectedTag?.facilities || []);
  }, [selectedCategory, tagFacilities]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '130px',
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragStartX(e.clientX);
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (Math.abs(e.clientX - dragStartX) > 5) {
      setIsDragging(true);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartX(e.touches[0].clientX);
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (Math.abs(e.touches[0].clientX - dragStartX) > 5) {
      setIsDragging(true);
    }
  };

  const onClickImg = (id: number) => {
    if (!isDragging) {
      navigate(`/facilities/${id}`);
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
  };

  const displayFacilities = selectedTagFacilities.length > 0 ? selectedTagFacilities : tagFacilities[0]?.facilities || [];

  // 추천 시설이 없으면 UI를 렌더링하지 않음
  if (tagFacilities.length === 0 || displayFacilities.length === 0) {
    return null;
  }

  return (
    <div className="MainBody">
      <A.Text>
        <A.GreenText>{isGuest ? '방문자' : userName}</A.GreenText>님, 오늘은<br />
        이런 시설 어때요?
      </A.Text>
      <A.Tag>
        {tagFacilities.map((tag) => (
          <A.button
            className="CircleButt"
            key={tag.tag}
            onClick={() => handleCategoryClick(tag.tag)}
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              backgroundColor: selectedCategory === tag.tag ? '#e8f5e9' : 'initial',
              color: selectedCategory === tag.tag ? 'green' : 'black',
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {`#${tag.tag}`}
          </A.button>
        ))}
      </A.Tag>
      <A.container>
        <A.CustomSlider {...settings}>
          {displayFacilities.map((facility) => (
              <A.component
                key={facility.id}
                onClick={() => onClickImg(facility.id)}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
              >
                <A.ImgWrapper>
                  <img src={facility.imageURL || 'default-image-url'} alt={`facility${facility.id}`} />
                  <A.FacilityName>{facility.name}</A.FacilityName>
                </A.ImgWrapper>
              </A.component>
            )
          )}
        </A.CustomSlider>
      </A.container>
    </div>
  );
};

export default Slide;
